import { Updates } from "@microsoft/fast-element";
import { Listbox as FluentListbox } from "@fluentui/web-components";
import { isDropdownOption } from "../option/index.js";
import { isOptionGroup } from "../option-group/option-group.options.js";
import { waitForConnectedDescendants } from "../../util/descendants.js";
import { toggleState } from "../../util/element-internals.js";
/**
 * Listbox
 * @summary A Listbox Custom HTML Element that allows for single or multiple selection from a list of options.
 * Extends the Fluent Listbox to support option groups ({@link @fabric-msft/fabric-web#OptionGroup}).
 *
 * @example
 * ```html
 * <fabric-listbox id="myListbox" multiple>
 *   <fabric-option value="option1">Option 1</fabric-option>
 *   <fabric-option value="option2">Option 2</fabric-option>
 *   <fabric-option value="option3">Option 3</fabric-option>
 * </fabric-listbox>
 * ```
 *
 * @example
 * ```html
 * <fabric-listbox>
 *   <fabric-option-group label="Fruits">
 *     <fabric-option value="apple">Apple</fabric-option>
 *     <fabric-option value="banana">Banana</fabric-option>
 *   </fabric-option-group>
 *   <fabric-option-group label="Vegetables">
 *     <fabric-option value="carrot">Carrot</fabric-option>
 *   </fabric-option-group>
 * </fabric-listbox>
 * ```
 *
 * @attr {string} id - Sets the listbox ID to a unique value if one is not provided.
 * @attr {boolean | undefined} multiple - Indicates whether the listbox allows multiple selection.
 *
 * @prop {Array<HTMLElement>} options - The collection of child options that are not disabled.
 * @prop {Array<HTMLElement>} selectedOptions - The collection of child options that are selected.
 * @prop {number} selectedIndex - The index of the currently selected option.
 *
 * @slot - The default slot for the options and option groups.
 *
 * @method multipleChanged - Updates the multiple selection state of the listbox and its options. Parameters: prev (boolean | undefined), next (boolean | undefined).
 * @method clickHandler - Sets the `selected` state on a target option when clicked. Parameters: e (PointerEvent).
 * @method selectOption - Selects an option by index. Parameters: index (number).
 *
 * @fires connected - Dispatched when the element is connected to the DOM.
 *
 * @extends FluentListbox
 * @tagname fabric-listbox
 * @public
 */
export class Listbox extends FluentListbox {
    /**
     * Checks if an option belongs to a disabled option group.
     *
     * @param option - The option to check.
     * @returns true if the option is inside a disabled option group.
     * @internal
     */
    isOptionInDisabledGroup(option) {
        return (isOptionGroup(option.parentElement) && !!option.parentElement.disabled);
    }
    /**
     * Sets the `selected` state on a target option when clicked.
     * Overrides the base implementation to prevent selection of options in disabled groups.
     *
     * @param e - The pointer event
     * @override
     * @public
     */
    clickHandler(e) {
        if (this.dropdown) {
            return true;
        }
        const target = e.target;
        if (isDropdownOption(target) && this.isOptionInDisabledGroup(target)) {
            return true;
        }
        return super.clickHandler(e);
    }
    /**
     * Selects an option by index.
     * Overrides the base implementation to prevent selection of options in disabled groups.
     *
     * @param index - The index of the option to select.
     * @override
     * @public
     */
    selectOption(index) {
        const option = this.enabledOptions[index !== null && index !== void 0 ? index : this.selectedIndex];
        if (option && this.isOptionInDisabledGroup(option)) {
            return;
        }
        super.selectOption(index);
    }
    /**
     * Handles the slotchange event for the listbox.
     * Overrides the base implementation to support option groups.
     * Extracts options from both direct option elements and option group children,
     * flattening them into a single options array while propagating group disabled states.
     *
     * @param e - The slot change event.
     * @override
     * @internal
     */
    slotchangeHandler(e) {
        const target = e.target;
        /**
         * waitForConnectedDescendants waits for element registration, Updates.enqueue waits for FAST's reactive bindings to complete. Both are needed because slotted directives populate asynchronously.
         */
        waitForConnectedDescendants(this, () => {
            // Defer to allow OptionGroup's slotted directive to populate
            Updates.enqueue(() => {
                const assigned = target.assignedElements();
                const options = [];
                for (const el of assigned) {
                    if (isOptionGroup(el)) {
                        const group = el;
                        options.push(...group.options);
                    }
                    else if (isDropdownOption(el)) {
                        options.push(el);
                    }
                }
                this.options = options;
            });
        });
    }
    /**
     * Handles changes to the options collection.
     * Overrides the base implementation to apply group-aware ARIA positioning.
     *
     * @param prev - The previous options collection.
     * @param next - The new options collection.
     * @override
     * @internal
     */
    optionsChanged(prev, next) {
        var _a;
        // Call the base implementation first
        (_a = super.optionsChanged) === null || _a === void 0 ? void 0 : _a.call(this, prev, next);
        // Apply group-scoped ARIA positioning
        if (next) {
            this.applyGroupScopedAria(next);
        }
    }
    /**
     * Build a list of groups and options
     * @param options
     * @param groups
     */
    setOptionGroups(options, groups) {
        for (const option of options) {
            const parent = option.parentElement;
            const groupKey = isOptionGroup(parent) ? parent : null;
            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey).push(option);
        }
    }
    /**
     * Applies group-scoped ARIA positioning attributes to options.
     * Options within the same group share aria-setsize scoped to that group.
     *
     * @param options - The flattened options collection.
     * @internal
     */
    applyGroupScopedAria(options) {
        // Group options by their parent option-group (or null for ungrouped)
        const groups = new Map();
        this.setOptionGroups(options, groups);
        // Apply ARIA attributes scoped to each group
        for (const [group, groupOptions] of groups) {
            const setSize = groupOptions.length;
            groupOptions.forEach((option, index) => {
                // set the position index
                option.elementInternals.ariaPosInSet = `${index + 1}`;
                // set the size of the group
                option.elementInternals.ariaSetSize = `${setSize}`;
                // Set aria-description to announce the group name for screen readers
                if (group && isOptionGroup(group) && group.label) {
                    option.elementInternals.ariaDescription = group.label;
                }
                else {
                    option.elementInternals.ariaDescription = null;
                }
            });
        }
    }
    /**
     * Updates the multiple selection state of the listbox and its options.
     *
     * @param prev - the previous multiple value
     * @param next - the current multiple value
     */
    multipleChanged(prev, next) {
        this.elementInternals.ariaMultiSelectable = next ? "true" : "false";
        toggleState(this.elementInternals, "multiple", next);
        Updates.enqueue(() => {
            var _a;
            (_a = this.options) === null || _a === void 0 ? void 0 : _a.forEach((x) => {
                x.multiple = !!next;
            });
        });
    }
}
//# sourceMappingURL=listbox.js.map